import React, { useEffect, useState } from "react";
import { Card, Typography, Row, Col, List } from "antd";
import {
  ThunderboltOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CameraOutlined,
  EnvironmentFilled,
  TeamOutlined,
  ShopOutlined,
  GlobalOutlined,
  LinkOutlined,
  RocketOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import blobBackground from "../../assets/images/gofurnishme_images/blob-scene-haikei-1.svg";
import { gutter_v2 } from "../../assets/images/home_images";
import AppStoreImage from "../../assets/images/Download_images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.jpg";
import GooglePlayImage from "../../assets/images/Download_images/GetItOnGooglePlay_Badge_Web_color_English.png";
import { analytics } from "../../firebase";
import { logEvent } from "firebase/analytics";

const { Title, Paragraph, Text } = Typography;

const BRAND = {
  gradient: "linear-gradient(135deg, #ff6f73 0%, #ff4757 100%)",
  shadow: "0 4px 20px rgba(255, 71, 87, 0.25)",
  color: "#ff4757",
};

const IconBox: React.FC<{ icon: React.ReactNode; size?: number }> = ({ icon, size = 56 }) => (
  <div
    style={{
      width: size, height: size, borderRadius: size * 0.28,
      background: BRAND.gradient,
      boxShadow: BRAND.shadow,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: size * 0.42, color: "#fff", display: "flex", alignItems: "center" }}>{icon}</span>
  </div>
);

const AboutUs: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (analytics) {
        logEvent(analytics as any, "page_view", {
          page_title: "About",
          page_location: window.location.href,
          page_path: window.location.pathname || "/about",
          app_platform: "web",
        });
      }
    } catch (_) {}
  }, []);

  const contactInfo = [
    { icon: <MailOutlined />, value: "support@throwly.com" },
    { icon: <EnvironmentOutlined />, value: "Ames, Iowa" },
  ];

  const faqData = [
    { key: "1", question: "Can you buy things outright?", answer: "Yes. On the Home screen, look in the listings. Items posted with a set price are sold for money. You can text the seller, negotiate if needed, and arrange payment using your agreed method." },
    { key: "2", question: "How do you post items?", answer: "Go to the Add Listing page (center button in the app). Upload images, add a title and description. If you leave price = 0 → it's a free item, AI assigns points. If you set a price → it's sold with money." },
    { key: "3", question: "How do you show interest in an item?", answer: "For paid items: Tap the seller's profile on the listing page, then hit Text to message them. For free items: You need enough points. If you do, you can text to arrange pickup. If you also have points for transport, you'll see a Checkout button." },
    { key: "4", question: "How do you transfer points?", answer: "After meeting and exchanging the item, open your chat with the seller and swipe up. This triggers the point transfer instantly." },
    { key: "5", question: "How many points do you start with?", answer: "Every user starts with $50 in points to begin donating, trading, and building community karma." },
    { key: "6", question: "How does Throwly ensure safety?", answer: "Accounts are verified. Most exchanges use points instead of real money. We discourage unsafe in-person meetups and scams." },
    { key: "7", question: "Who wins prizes?", answer: "Each month, the top donor (most points per city) wins community prizes." },
    { key: "8", question: "How/What location info is shown?", answer: "Item locations only appear if the seller chooses to show them. Any item shown on the map stays visible for 7 days. For free items, showing how close it is helps the item get picked up faster. For paid items, sellers can still opt in for extra visibility, with the understanding of the added exposure. We never show or share your exact personal location. For tweets, we only show approximate distance. If it's under 5 miles, we say 'in town.' On listing screens, we display only how far away the item is—not your address." },
    { key: "9", question: "How does Throwly use my location?", answer: "To find nearby items. To calculate transport costs. To show local events, services, and volunteer gigs. To run city-specific leaderboards, prizes, and notifications." },
    { key: "10", question: "How does Throwly use notifications?", answer: "To alert you when buyers or sellers send you messages." },
    { key: "11", question: "How do you stop people from gaming the system?", answer: "We track device usage and ID verification (e.g., phone, DL in the future)." },
    { key: "12", question: "Why Throwly?", answer: "Throwly builds the world's biggest free marketplace—rewarding givers, empowering communities, and removing the pain of scams, no-shows, ghosting, haggling, and unsafe exchanges." },
    { key: "13", question: "Do you get rewards for referrals?", answer: "Yes! You earn $10 in points for each friend you refer." },
  ];

  const mediaLinks = [
    { title: "Applied AI Challenge - Congratulations to 2025 Participants", url: "https://www.facebook.com/CyclonesInnov8/posts/congratulations-to-the-2025-applied-ai-challenge-participants-and-scholarship-wi/1207285808067166/", description: "Cyclones Innov8 celebrates the 2025 Applied AI Challenge participants and scholarship winners." },
    { title: "Applied AI Challenge Showcases Student Innovation", url: "https://sictr.iastate.edu/2025/03/20/applied-ai-challenge-showcases-student-innovation/", description: "SICTR highlights student innovation at the Applied AI Challenge showcase." },
    { title: "Innovate at Iowa State", url: "https://sictr.iastate.edu/innovate-at-iowa-state/", description: "Discover innovation opportunities at Iowa State University." },
    { title: "LinkedIn Activity - Innovation Update", url: "https://www.linkedin.com/feed/update/urn:li:activity:6923745918873735168/", description: "LinkedIn post about innovation and development progress." },
    { title: "LinkedIn Activity - Project Update", url: "https://www.linkedin.com/feed/update/urn:li:activity:7308874340756770816/", description: "Recent project updates and achievements shared on LinkedIn." },
    { title: "Vibe Coding: Senior Devs as AI Babysitters", url: "https://techcrunch.com/2025/09/14/vibe-coding-has-turned-senior-devs-into-ai-babysitters-but-they-say-its-worth-it/", description: "TechCrunch explores how AI is transforming the role of senior developers." },
    { title: "Earth Day Celebrations", url: "https://iowastatedaily.com/317511/news/see-whats-store-for-earth-day-celebrations/", description: "Iowa State Daily coverage of Earth Day celebration events." },
  ];

  const innovations = [
    { icon: <CameraOutlined />, title: "Snap and Price", description: "Our AI analyzes any item photo and instantly suggests a fair market value." },
    { icon: <EnvironmentFilled />, title: "Location & Time Smart", description: "Prices adjust automatically based on your marketplace and geographic location." },
    { icon: <TeamOutlined />, title: "Community Verified", description: "Human in the loop users validate suggestions, keeping prices honest and accurate over time." },
    { icon: <ShopOutlined />, title: "Vendor Friendly", description: "No more guesswork for sellers. Set fair prices that attract buyers quickly." },
  ];

  const stats = [
    { value: "$50", label: "In points to start" },
    { value: "$10", label: "Earned per referral" },
    { value: "1st", label: "Point-based AI marketplace" },
  ];

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <style>{`
        .ab-card { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .ab-card:hover { transform: translateY(-4px); box-shadow: 0 16px 48px rgba(0,0,0,0.11) !important; }
        .ab-inno { transition: background 0.2s ease, transform 0.2s ease; }
        .ab-inno:hover { background: #fff1f1 !important; transform: translateY(-2px); }
        .ab-media { transition: transform 0.2s ease, box-shadow 0.2s ease; cursor: pointer; }
        .ab-media:hover { transform: translateY(-3px); box-shadow: 0 8px 28px rgba(255,71,87,0.14) !important; }
        .ab-pill { display: inline-flex; align-items: center; gap: 6px; padding: 7px 18px; background: rgba(255,255,255,0.72); border: 1px solid rgba(0,0,0,0.1); border-radius: 999px; color: #1a202c; font-size: 13px; font-weight: 500; backdrop-filter: blur(6px); }
.ab-store-badge { transition: transform 0.2s ease; }
        .ab-store-badge:hover { transform: scale(1.04); }
      `}</style>

      {/* HERO */}
      <div
        style={{
          backgroundImage: `url(${blobBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: 500,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", padding: "72px 24px 80px", maxWidth: 680, margin: "0 auto" }}>
          <img
            src={gutter_v2}
            alt="Throwly"
            style={{ height: 400, width: "auto", marginBottom: 24, objectFit: "contain", display: "block", margin: "0 auto 24px" }}
          />
          <Title
            level={1}
            style={{ color: "#1a202c", fontWeight: 800, fontSize: "clamp(34px, 5vw, 54px)", margin: "0 0 14px", lineHeight: 1.1 }}
          >
            About Us
          </Title>
          <Paragraph style={{ color: "#475569", fontSize: 18, margin: "0 0 36px", lineHeight: 1.65 }}>
            We believe the future of local e-commerce is free, sustainable, and rewarding.
          </Paragraph>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <span className="ab-pill"><EnvironmentOutlined /> Born in Ames, Iowa</span>
            <span className="ab-pill"><ThunderboltOutlined /> AI-Powered</span>
            <span className="ab-pill"><TeamOutlined /> Community First</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "64px 24px 80px" }}>

        {/* STATS BAR */}
        <div
          className="ab-card"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            borderRadius: 20,
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            marginBottom: 40,
            background: BRAND.gradient,
          }}
        >
          {stats.map((s, i) => (
            <div
              key={i}
              style={{
                padding: "38px 24px",
                textAlign: "center",
                borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.22)" : "none",
              }}
            >
              <div style={{ fontSize: 44, fontWeight: 800, color: "#fff", lineHeight: 1, marginBottom: 8 }}>{s.value}</div>
              <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 14, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* OUR STORY */}
        <Card
          className="ab-card"
          style={{ borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #edf0f5", marginBottom: 40, overflow: "hidden" }}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ height: 6, background: BRAND.gradient }} />
          <div style={{ padding: "52px 60px" }}>
            <Title level={2} style={{ fontWeight: 800, color: "#1a202c", fontSize: 28, textAlign: "center", marginBottom: 36 }}>
              Our Story
            </Title>
            <div style={{ maxWidth: 760, margin: "0 auto" }}>
              <blockquote
                style={{
                  borderLeft: `4px solid ${BRAND.color}`,
                  paddingLeft: 24,
                  margin: "0 0 32px",
                  fontStyle: "italic",
                  fontSize: 19,
                  color: "#475569",
                  lineHeight: 1.65,
                }}
              >
                "Why should generosity be a one-way street?"
              </blockquote>
              <Paragraph style={{ color: "#64748b", lineHeight: 1.9, fontSize: 16, marginBottom: 18 }}>
                Throwly was born from a simple observation: students and neighbors were giving away perfectly good items for free—only to later buy the same things again at full price. We set out to create a smarter solution.
              </Paragraph>
              <Paragraph style={{ color: "#64748b", lineHeight: 1.9, fontSize: 16, marginBottom: 18 }}>
                What started as an idea in Ames, Iowa grew into the first point-based, AI-powered marketplace designed for real communities. Today, Throwly helps people turn old dorm furniture into points for kitchen gear, or swap unused clothes for something new—all while reducing waste and building stronger local connections.
              </Paragraph>
              <Paragraph style={{ color: "#64748b", lineHeight: 1.9, fontSize: 16, margin: 0 }}>
                Throwly isn't just a marketplace—it's a movement to make giving back easy, rewarding, and fun.
              </Paragraph>
            </div>
          </div>
        </Card>

        {/* MISSION + VISION */}
        <Row gutter={[28, 28]} style={{ marginBottom: 40 }}>
          <Col xs={24} md={12}>
            <Card
              className="ab-card"
              style={{ borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #edf0f5", height: "100%" }}
              bodyStyle={{ padding: "44px 40px" }}
            >
              <IconBox icon={<RocketOutlined />} />
              <Title level={3} style={{ fontWeight: 700, color: "#1a202c", fontSize: 22, margin: "20px 0 14px" }}>
                Our Mission
              </Title>
              <Paragraph style={{ color: "#64748b", lineHeight: 1.85, fontSize: 15, margin: 0 }}>
                Remove friction from local exchanges and make generosity rewarding. With AI-powered pricing and a point-based system, Throwly reimagines Facebook Marketplace for the future—helping you list, earn, and redeem with ease.
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              className="ab-card"
              style={{ borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #edf0f5", height: "100%" }}
              bodyStyle={{ padding: "44px 40px" }}
            >
              <IconBox icon={<GlobalOutlined />} />
              <Title level={3} style={{ fontWeight: 700, color: "#1a202c", fontSize: 22, margin: "20px 0 14px" }}>
                Our Vision
              </Title>
              <Paragraph style={{ color: "#64748b", lineHeight: 1.85, fontSize: 15, margin: 0 }}>
                A future where local e-commerce is free, sustainable, and rewarding. Throwly turns everyday giving into community impact—making it easy to share, reuse, and connect. Every exchange reduces waste, saves money, and strengthens local bonds.
              </Paragraph>
            </Card>
          </Col>
        </Row>

        {/* KEY INNOVATIONS */}
        <Card
          className="ab-card"
          style={{ borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #edf0f5", marginBottom: 40 }}
          bodyStyle={{ padding: "52px 60px" }}
        >
          <Title level={2} style={{ fontWeight: 800, color: "#1a202c", fontSize: 28, textAlign: "center", marginBottom: 40 }}>
            Key Innovations
          </Title>
          <Row gutter={[20, 20]}>
            {innovations.map((item, i) => (
              <Col xs={24} md={12} key={i}>
                <div
                  className="ab-inno"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 18,
                    padding: "28px 24px",
                    borderRadius: 16,
                    background: "#f8fafc",
                    border: "1px solid #edf0f5",
                  }}
                >
                  <IconBox icon={item.icon} size={48} />
                  <div>
                    <Text strong style={{ fontSize: 15, color: "#1a202c", display: "block", marginBottom: 6 }}>{item.title}</Text>
                    <Text style={{ fontSize: 14, color: "#64748b", lineHeight: 1.65, display: "block" }}>{item.description}</Text>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Card>

        {/* MEDIA COVERAGE */}
        <Card
          className="ab-card"
          style={{ borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #edf0f5", marginBottom: 40 }}
          bodyStyle={{ padding: "52px 60px" }}
        >
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <IconBox icon={<GlobalOutlined />} />
            <Title level={2} style={{ fontWeight: 800, color: "#1a202c", fontSize: 28, margin: "20px 0 10px" }}>
              Media Coverage
            </Title>
            <Paragraph style={{ color: "#64748b", fontSize: 15, maxWidth: 560, margin: "0 auto" }}>
              Learn more about our journey, achievements, and the impact we're making in the community.
            </Paragraph>
          </div>
          <List
            grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
            dataSource={mediaLinks}
            renderItem={(item) => (
              <List.Item>
                <div
                  className="ab-media"
                  onClick={() => window.open(item.url, "_blank")}
                  style={{
                    borderRadius: 14, border: "1px solid #edf0f5",
                    padding: "18px 20px", background: "#fff",
                    display: "flex", alignItems: "flex-start", gap: 14,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: "linear-gradient(135deg, #fff1f1, #ffd6d8)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <LinkOutlined style={{ color: BRAND.color, fontSize: 15 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 13, color: "#1a202c", display: "block", marginBottom: 4, lineHeight: 1.4 }}>
                      {item.title}
                    </Text>
                    <Text style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5, display: "block" }}>
                      {item.description}
                    </Text>
                  </div>
                </div>
              </List.Item>
            )}
          />
        </Card>


        {/* FAQ */}
        <Card
          className="ab-card"
          style={{ borderRadius: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", border: "1px solid #edf0f5", marginBottom: 40 }}
          bodyStyle={{ padding: "52px 60px" }}
        >
          <Title level={2} style={{ fontWeight: 800, color: "#1a202c", fontSize: 28, textAlign: "center", marginBottom: 40 }}>
            Frequently Asked Questions
          </Title>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {faqData.map((faq) => {
              const isOpen = openFaq === faq.key;
              return (
                <div
                  key={faq.key}
                  onClick={() => setOpenFaq(isOpen ? null : faq.key)}
                  style={{
                    borderRadius: 14,
                    border: `1px solid ${isOpen ? BRAND.color : "#edf0f5"}`,
                    borderLeft: `4px solid ${isOpen ? BRAND.color : "#edf0f5"}`,
                    background: isOpen ? "#fffafa" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    overflow: "hidden",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "18px 22px" }}>
                    <div
                      style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: isOpen ? BRAND.gradient : "#f1f5f9",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "background 0.2s ease",
                      }}
                    >
                      {isOpen
                        ? <MinusOutlined style={{ fontSize: 11, color: "#fff" }} />
                        : <PlusOutlined style={{ fontSize: 11, color: "#94a3b8" }} />
                      }
                    </div>
                    <Text style={{ fontSize: 15, fontWeight: 600, color: "#1a202c", flex: 1, lineHeight: 1.45 }}>
                      {faq.question}
                    </Text>
                  </div>
                  {isOpen && (
                    <div style={{ padding: "0 22px 20px 66px" }}>
                      <Text style={{ fontSize: 14, color: "#64748b", lineHeight: 1.8, display: "block" }}>
                        {faq.answer}
                      </Text>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* DOWNLOAD CTA */}
        <div
          style={{
            borderRadius: 24,
            background: BRAND.gradient,
            padding: "64px 48px",
            textAlign: "center",
            boxShadow: "0 12px 48px rgba(255,71,87,0.28)",
          }}
        >
          <Title
            level={2}
            style={{ color: "#fff", fontWeight: 800, fontSize: "clamp(26px, 4vw, 38px)", margin: "0 0 12px", lineHeight: 1.15 }}
          >
            Discover great finds today!
          </Title>
          <Paragraph style={{ color: "rgba(255,255,255,0.85)", fontSize: 17, margin: "0 0 40px", lineHeight: 1.6 }}>
            Join the community. Start with $50 in points—no strings attached.
          </Paragraph>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginBottom: 36 }}>
            <a
              href="https://apps.apple.com/us/app/throwly/id1615326703?platform=iphone"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={AppStoreImage}
                alt="Download on the App Store"
                className="ab-store-badge"
                style={{ height: 52, borderRadius: 8, objectFit: "contain", display: "block" }}
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.Gutter"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={GooglePlayImage}
                alt="Get it on Google Play"
                className="ab-store-badge"
                style={{ height: 52, borderRadius: 8, objectFit: "contain", display: "block" }}
              />
            </a>
          </div>
          <div style={{ display: "flex", gap: 28, justifyContent: "center", flexWrap: "wrap" }}>
            {contactInfo.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.82)", fontSize: 14 }}>
                {c.icon}
                <span>{c.value}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutUs;
